import { match, P } from 'ts-pattern';
import { Address, Hex } from 'viem';
import { DEFAULT_SUBACCOUNT, ORDER_STOP_TYPES } from '../constants';
import { Position } from '../hooks/usePositions';
import { ProductType } from '../hooks/useProducts';
import { OrderFills } from '../types/fills';
import { OrderSide } from '../types/generated';
import { OrderStopType, ValidatedOrder } from '../types/orders';
import { formatDate, formatDollar, formatNumber, formatWrappedToken } from './formatters';
import { divide, getDecimalPlaces, multiply } from './math';
import { CustomToastOptions } from './showToast';
import { decodeSubaccountName } from './subaccountHelpers';

export const createdOrderToToastMessage = (product: ProductType, order: ValidatedOrder) => {
  const side = order.side === OrderSide.BUY ? 'LONG' : 'SHORT';
  const qtyDecimals = getDecimalPlaces(product.lotSize);
  const priceDecimals = getDecimalPlaces(product.tickSize);

  const quantity =
    'close' in order && order.close
      ? 'Close'
      : formatNumber(order.amount, {
          minimumFractionDigits: qtyDecimals,
          maximumFractionDigits: qtyDecimals,
        });
  const price =
    'price' in order &&
    formatDollar(order.price, {
      minimumFractionDigits: priceDecimals,
      maximumFractionDigits: priceDecimals,
    });

  return {
    variant: 'success',
    message: `${quantity} ${order.type} ${side} placed${price ? ` @ ${price}` : ''}`,
  } satisfies CustomToastOptions;
};

export const batchedFilledOrderToToastMessage = (product: ProductType, fills: OrderFills) => {
  const firstFill = fills.data[0];
  if (!firstFill) {
    throw new Error('No fills provided');
  }

  // Use the side from the first fill (all fills in a batch should have the same side, as they are grouped by order)
  const side = firstFill.side === OrderSide.BUY ? 'LONG' : 'SHORT';

  // Calculate total quantity
  const totalFilled = fills.data.reduce((sum, fill) => sum + fill.filled, 0n);

  // Calculate weighted average price
  const totalValue = fills.data.reduce((sum, fill) => sum + multiply(fill.price, fill.filled), 0n);
  const avgPrice = divide(totalValue, totalFilled);

  const sizeDecimals = getDecimalPlaces(product.lotSize);
  const priceDecimals = getDecimalPlaces(product.tickSize);

  const formattedTotalFilled = formatNumber(totalFilled, {
    maximumFractionDigits: sizeDecimals,
    minimumFractionDigits: sizeDecimals,
  });
  const formattedAvgPrice = formatDollar(avgPrice, {
    maximumFractionDigits: priceDecimals,
    minimumFractionDigits: priceDecimals,
  });

  // Create message in the same format as the original
  let message = `${product.displayTicker}: ${formattedTotalFilled} ${side} filled @ ${formattedAvgPrice}`;

  // Add fill count if there are multiple fills
  if (fills.data.length > 1) {
    message += ` (${fills.data.length} fills)`;
  }

  return {
    variant: 'success',
    message,
  } satisfies CustomToastOptions;
};

export const tokenApprovalSuccessToastMessage = () => {
  return {
    variant: 'success',
    message: 'Approval successful, depositing...',
  } satisfies CustomToastOptions;
};

export const tokenApprovalFailedToastMessage = () => {
  return {
    variant: 'error',
    message: 'Something went wrong while approving the token',
  } satisfies CustomToastOptions;
};

export const depositSuccessToastMessage = (
  subaccount: Hex = DEFAULT_SUBACCOUNT,
  symbol: string,
  amount: bigint
) => {
  return {
    variant: 'success',
    title: 'Deposit Successful',
    message: (
      <>
        Deposited{' '}
        <span className="brightness-200">
          {formatNumber(amount)} {symbol}
        </span>{' '}
        to {decodeSubaccountName(subaccount)} account.
      </>
    ),
    // actions: [{ label: 'View explorer', href: 'TODO' }],
  } satisfies CustomToastOptions;
};

export const orderCancellationSuccessToastMessage = (numberOfOrder: number) => {
  return {
    variant: 'success',
    message: numberOfOrder > 1 ? `Canceled ${numberOfOrder} orders` : 'Order canceled',
  } satisfies CustomToastOptions;
};

export const orderCancellationPartialSuccessToastMessage = (
  numSuccess: number,
  numFailed: number
) => {
  const successMessage =
    numSuccess > 0
      ? numSuccess > 1
        ? `${numSuccess} orders canceled. `
        : `1 order canceled. `
      : '';
  const failureMessage =
    numFailed > 1 ? `${numFailed} orders failed to cancel` : `1 order failed to cancel`;
  return {
    variant: numSuccess > 0 ? 'info' : 'error',
    message: `${successMessage}${failureMessage}`,
  } satisfies CustomToastOptions;
};

export const cancelRejectedWithReasonToastMessage = (reason: string | undefined) => {
  return {
    variant: 'error',
    message: `Failed to cancel order${reason ? `: ${reason}` : ''}`,
  } satisfies CustomToastOptions;
};

export const orderCancellationFailedToastMessage = () => {
  return {
    variant: 'error',
    message: 'Something went wrong while cancelling order',
  } satisfies CustomToastOptions;
};

export const withdrawClaimingSuccessToastMessage = (
  to: Address,
  symbol: string,
  amount: bigint
) => {
  return {
    variant: 'success',
    title: 'Withdraw successfully claimed',
    message: (
      <>
        Withdrew{' '}
        <span className="brightness-200">
          {formatNumber(amount)} {formatWrappedToken(symbol)}
        </span>{' '}
        to {to}.
      </>
    ),
  } satisfies CustomToastOptions;
};

export const withdrawClaimingFailedToastMessage = () => {
  return {
    variant: 'error',
    message: 'Something went wrong while claiming withdraw',
  } satisfies CustomToastOptions;
};

export const userRejectedSignatureToastMessage = (action: string) => {
  return {
    variant: 'error',
    message: `Signature is required to ${action}`,
  } satisfies CustomToastOptions;
};

export const enableOneClickSigningFailedToastMessage = (errorMessage?: string) => {
  return {
    variant: 'error',
    message: errorMessage || 'Failed to enable one-click signing, this error has been logged.',
  } satisfies CustomToastOptions;
};

export const disableOneClickSigningFailedToastMessage = () => {
  return {
    variant: 'error',
    message: 'Failed to disable one-click signing, this error has been logged.',
  } satisfies CustomToastOptions;
};

export const enableOneClickTradingSuccessToastMessage = () => {
  return {
    variant: 'success',
    message: 'One-Click Trading enabled, onboarding completed.',
  } satisfies CustomToastOptions;
};

export const disableOneClickTradingSuccessToastMessage = () => {
  return {
    variant: 'success',
    message: 'One-Click Trading has been disabled.',
  } satisfies CustomToastOptions;
};

export const revokeSignerSuccessToastMessage = () => {
  return {
    variant: 'success',
    message: 'Trading sessions has been revoked.',
  } satisfies CustomToastOptions;
};

export const enableTradingSuccessToastMessage = () => {
  return { variant: 'success', message: 'All done, trade away.' } satisfies CustomToastOptions;
};

export const withdrawFromSubAccountFailedToastMessage = (subAccountName: Hex | undefined) => {
  return {
    variant: 'error',
    message: `Failed to withdraw${subAccountName ? ' ' + decodeSubaccountName(subAccountName) : '.'}`,
  } satisfies CustomToastOptions;
};

export const createOrderCanceledToastMessage = () => {
  return { variant: 'error', message: 'Order canceled.' } satisfies CustomToastOptions;
};

export const createOrderFailedToastMessage = (
  message = 'An unexpected error occurred while placing your order. Our team has been notified and is working to resolve the issue.',
  filled?: bigint
) => {
  return {
    // If the order was partially filled before hitting error, we show warning
    // instead of error.
    variant: filled !== 0n ? 'warning' : 'error',
    message,
  } satisfies CustomToastOptions;
};

export const createMintingFailedToastMessage = () => {
  return {
    variant: 'error',
    message: `Something went wrong while minting.`,
  } satisfies CustomToastOptions;
};

export const createMintingCanceledToastMessage = () => {
  return { message: `Mint canceled.` } satisfies CustomToastOptions;
};

export const createMintingSuccessToastMessage = ({
  amount,
  symbol,
}: {
  amount: string;
  symbol: string;
}) => {
  return {
    variant: 'success',
    message: `Minted ${amount} ${symbol}.`,
  } satisfies CustomToastOptions;
};

export const positionStopOrderSuccess = ({
  stopType,
  stopPrice,
  priceDecimals,
}: {
  stopType: OrderStopType;
  stopPrice: bigint;
  priceDecimals: number;
}) => {
  return {
    variant: 'success',
    message: `Position ${stopType === ORDER_STOP_TYPES.GAIN ? 'take profit' : 'stop loss'} order @ ${formatDollar(stopPrice, { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals })} successfully created.`,
  } satisfies CustomToastOptions;
};

// Combined error toast for both TP and SL
export const positionBothStopOrdersError = () => {
  return {
    variant: 'error',
    message: 'Failed to create take profit and stop loss. Please try again.',
  } satisfies CustomToastOptions;
};

// Individual error toasts for TP and SL
export const positionStopOrderError = ({ stopType }: { stopType: OrderStopType }) => {
  return {
    variant: 'error',
    message: `Failed to create ${stopType === ORDER_STOP_TYPES.GAIN ? 'take profit' : 'stop loss'}. Please try again.`,
  } satisfies CustomToastOptions;
};

type PositionStopOrdersSuccessInput = {
  tpOutcome: 'created' | 'closed' | 'unchanged' | 'failed';
  slOutcome: 'created' | 'closed' | 'unchanged' | 'failed';
  tpPrice: bigint | undefined;
  slPrice: bigint | undefined;
  priceDecimals: number;
};
export const positionStopOrdersSuccess = ({
  tpOutcome,
  slOutcome,
  tpPrice,
  slPrice,
  priceDecimals,
}: PositionStopOrdersSuccessInput): CustomToastOptions => {
  const formatPrice = (price: bigint | undefined) =>
    formatDollar(price, {
      minimumFractionDigits: priceDecimals,
      maximumFractionDigits: priceDecimals,
    });

  const message = match({ tpOutcome, slOutcome })
    .returnType<string>()
    // --- Both Created ---
    .with(
      { tpOutcome: 'created', slOutcome: 'created' },
      () => `Take profit set @ ${formatPrice(tpPrice)} and stop loss set @ ${formatPrice(slPrice)}.`
    )
    // --- One Created, One Closed ---
    .with(
      { tpOutcome: 'created', slOutcome: 'closed' },
      () => `Take profit set @ ${formatPrice(tpPrice)} and stop loss removed.`
    )
    .with(
      { tpOutcome: 'closed', slOutcome: 'created' },
      () => `Stop loss set @ ${formatPrice(slPrice)} and take profit removed.`
    )
    // --- One Created, One Unchanged or Failed (failures get a separate toast) ---
    .with(
      { tpOutcome: 'created', slOutcome: P.union('unchanged', 'failed') },
      () => `Take profit set @ ${formatPrice(tpPrice)}.`
    )
    .with(
      { tpOutcome: P.union('unchanged', 'failed'), slOutcome: 'created' },
      () => `Stop loss set @ ${formatPrice(slPrice)}.`
    )
    // --- Both Closed ---
    .with(
      { tpOutcome: 'closed', slOutcome: 'closed' },
      () => `Take profit and stop loss both removed.`
    )
    // --- One Closed, One Unchanged or Failed (failures get a separate toast) ---
    .with(
      { tpOutcome: 'closed', slOutcome: P.union('unchanged', 'failed') },
      () => `Take profit removed.`
    )
    .with(
      { tpOutcome: P.union('unchanged', 'failed'), slOutcome: 'closed' },
      () => `Stop loss removed.`
    )
    // --- Exhaustive cases ---
    // This should never happen
    .with(
      { tpOutcome: P.union('unchanged', 'failed'), slOutcome: P.union('unchanged', 'failed') },
      () => {
        throw new Error(
          `Both stop orders are unchanged | failed. This should not happen. tpOutcome: ${tpOutcome} |  slOutcome: ${slOutcome}`
        );
      }
    )
    .exhaustive(); // Ensures all success combinations are covered

  return {
    variant: 'success',
    message,
  } satisfies CustomToastOptions;
};

// Cancellation error toast
export const positionStopOrdersCancellationError = () => {
  return {
    variant: 'error',
    message: 'Failed to cancel existing stop triggers. Please try again.',
  } satisfies CustomToastOptions;
};

export const closePositionsToast = ({
  successfulPositionIds,
  failedPositionIds,
}: {
  successfulPositionIds: string[];
  failedPositionIds: string[];
}): CustomToastOptions => {
  const message = match({
    hasSuccesses: successfulPositionIds.length > 0,
    hasFailures: failedPositionIds.length > 0,
  })
    .returnType<CustomToastOptions>()
    .with({ hasSuccesses: true, hasFailures: false }, () => ({
      message: `Closed all ${successfulPositionIds.length} positions`,
      variant: 'success',
    }))
    .with({ hasSuccesses: true, hasFailures: true }, () => ({
      message: `Closed ${successfulPositionIds.length} positions, ${failedPositionIds.length} failed`,
      variant: 'warning',
    }))
    .with({ hasSuccesses: false, hasFailures: true }, () => ({
      message: `Failed to close all positions`,
      variant: 'error',
    }))
    .otherwise(() => ({
      message: 'No positions to close',
      variant: 'info',
    }));

  return message satisfies CustomToastOptions;
};

export const liquidatedToasts = (
  positions: Position[] | undefined,
  products: Record<string, { displayTicker?: string; baseTokenName?: string }> | undefined,
  liquidatedAt: number
): Array<CustomToastOptions> => {
  // If 1-2 positions, return individual toasts for each position.
  // We may want to change this to just 1.
  if (positions && positions.length && positions.length <= 2) {
    return positions.map((position) => {
      const product = products?.[position.productId];

      return {
        variant: 'error',
        message: `${product?.displayTicker ? `${product.displayTicker} ` : ''}${position.side === OrderSide.BUY ? 'Long' : 'Short'} position liquidated at ${formatDate(liquidatedAt)}`,
        // Sticky
        duration: Infinity,
      };
    });
  }

  // If more than 3 positions, or positions not fetched, show account liquidated
  // toast
  return [
    {
      variant: 'error',
      message: `${positions && positions.length > 0 ? `${positions.length} positions ` : 'Subaccount'} liquidated at ${formatDate(liquidatedAt)}`,
      // Sticky
      duration: Infinity,
    },
  ];
};

export const walletUnexpectedlyNotConnectedToast = () => {
  return {
    variant: 'error',
    message: 'Wallet is not connected, please check your wallet and connect again.',
  } satisfies CustomToastOptions;
};

export const signerRenewedToast = () => {
  return {
    variant: 'success',
    message: 'Signer successfully renewed.',
  } satisfies CustomToastOptions;
};
